import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";

const REQUIRED_PHRASE = "RESET PRODUCTION TO CLEAN START";
const BROKER_OPTIONS = [
  { key: "preserve_with_baseline", label: "Preserve credentials + rebase" },
  { key: "preserve_full",          label: "Preserve broker + history" },
  { key: "disconnect",             label: "Disconnect + wipe" },
  { key: "cancel",                 label: "Cancel migration" },
];

export default function MigrationPanel() {
  const [state, setState] = useState(null);
  const [preview, setPreview] = useState(null);
  const [step, setStep] = useState(1);  // 1=preview 2=broker 3=confirm 4=execute 5=verify
  const [brokerChoice, setBrokerChoice] = useState("");
  const [preserveScannerProfiles, setPreserveScannerProfiles] = useState(true);
  const [reason, setReason] = useState("");
  const [typed, setTyped] = useState("");
  const [backupRef, setBackupRef] = useState("");
  const [idem, setIdem] = useState("");
  const [run, setRun] = useState(null);
  const [verify, setVerify] = useState(null);
  const [busy, setBusy] = useState(false);
  const [locked, setLocked] = useState(false);
  const [unlockReason, setUnlockReason] = useState("");

  const loadState = async () => {
    try {
      const r = await api.get("/admin/production-clean-start/state");
      setState(r.data);
    } catch (e) { toast.error("Failed to load migration state"); }
  };
  useEffect(() => { loadState(); }, []);

  const runPreview = async () => {
    setBusy(true);
    try {
      const r = await api.post("/admin/production-clean-start/preview", {
        preserve_broker_connection: brokerChoice || null,
        preserve_scanner_profiles: preserveScannerProfiles,
      });
      setPreview(r.data);
      setStep(2);
    } catch (e) { toast.error(e?.response?.data?.detail || "Preview failed"); }
    finally { setBusy(false); }
  };

  const runConfirm = async () => {
    setBusy(true);
    try {
      const r = await api.post("/admin/production-clean-start/confirm", {
        preview_id: preview?.preview_id,
        idempotency_key: idem,
        typed_confirmation: typed,
        reason,
        preserve_broker_connection: brokerChoice,
        preserve_scanner_profiles: preserveScannerProfiles,
        external_backup_reference: backupRef,
        allow_non_production: state?.environment !== "production",
      });
      setRun(r.data.run);
      toast.success("Migration executed");
      setStep(5);
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail || "Confirm failed";
      if (status === 423) {
        setLocked(true);
        toast.error("Migration locked — use Danger Zone unlock below");
      } else if (status === 409) {
        toast.warning("Preview outdated — please re-run preview");
        setStep(1);
        setPreview(null);
      } else {
        toast.error(detail);
      }
    }
    finally { setBusy(false); }
  };

  const runUnlock = async () => {
    setBusy(true);
    try {
      await api.post("/admin/production-clean-start/unlock", {
        typed_confirmation: "ENABLE PRODUCTION RESET",
        reason: unlockReason,
      });
      toast.success("Wizard unlocked. You may run a new preview.");
      setLocked(false);
      setUnlockReason("");
      await loadState();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Unlock failed");
    } finally { setBusy(false); }
  };

  const runVerify = async () => {
    if (!run) return;
    setBusy(true);
    try {
      const r = await api.post(`/admin/production-clean-start/${run.id}/verify`);
      setVerify(r.data);
      if (r.data.overall === "PASS") toast.success("Verification PASS");
      else toast.warning(`Verification failed: ${r.data.passed}/${r.data.total}`);
    } catch (e) { toast.error("Verify failed"); }
    finally { setBusy(false); }
  };

  const canConfirm = typed === REQUIRED_PHRASE
    && backupRef.length >= 8
    && idem.length >= 6
    && reason.trim().length >= 6
    && brokerChoice && brokerChoice !== "cancel";

  return (
    <div className="space-y-3 p-3" data-testid="migration-panel">
      {/* State card */}
      <div className="border border-border/60 p-3 space-y-1" data-testid="migration-state">
        <div className="text-[10px] mono uppercase tracking-widest text-muted-foreground">Install State</div>
        {state ? (
          <>
            <div className="text-sm">
              Environment: <span className="mono">{state.environment}</span> ·
              Database: <span className="mono">{state.database_name || "—"}</span> ·
              Mode: <span className="mono">{state.install_mode}</span>
            </div>
            <div className="text-[10px] text-muted-foreground mono">
              demo_users={state.signals.demo_users} · harness_positions={state.signals.harness_positions} · ai_calls={state.signals.ai_calls}
            </div>
            {state.cutover && (
              <div className="text-[10px] text-emerald-500 mono">
                Cutover recorded {state.cutover.cutover_at} by {state.cutover.cutover_by_email}
              </div>
            )}
          </>
        ) : <div className="text-xs text-muted-foreground">Loading…</div>}
      </div>

      {/* Stepper */}
      <div className="border border-border/60 p-3">
        {step === 1 && (
          <div className="space-y-2">
            <div className="text-[10px] mono uppercase tracking-widest text-muted-foreground">Step 1 — Preview</div>
            <label className="text-xs flex items-center gap-2">
              <input type="checkbox" checked={preserveScannerProfiles}
                       onChange={e => setPreserveScannerProfiles(e.target.checked)}
                       data-testid="migration-preserve-scanner"/>
              Preserve scanner profile definitions
            </label>
            <button onClick={runPreview} disabled={busy}
                       className="border border-border/60 px-2 py-1 text-[10px] uppercase tracking-widest hover:bg-border/20 disabled:opacity-40"
                       data-testid="migration-preview-btn">
              {busy ? "…" : "Run Preview"}
            </button>
          </div>
        )}
        {step >= 2 && preview && (
          <div className="space-y-2">
            <div className="text-[10px] mono uppercase tracking-widest text-muted-foreground">Preview — {preview.preview_id}</div>
            <div className="text-xs">
              <span className="pnl-down">{preview.totals.delete}</span> delete ·
              <span className="ml-1">{preview.totals.reset}</span> reset ·
              <span className="pnl-up ml-1">{preview.totals.preserve}</span> preserve
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-1 text-[10px] max-h-48 overflow-auto border border-border/40 p-1" data-testid="migration-categories">
              {preview.categories.map(c => (
                <div key={c.key} className="flex justify-between border-b border-border/20 py-0.5">
                  <span>{c.label}</span>
                  <span className={c.action === "delete" ? "pnl-down" : "text-muted-foreground"}>{c.count} · {c.action}</span>
                </div>
              ))}
            </div>
            <div className="text-[10px] mono uppercase tracking-widest text-muted-foreground pt-1">Step 2 — Broker Handling</div>
            {preview.broker.current ? (
              <div className="text-[10px] text-muted-foreground mono">
                Current: {preview.broker.current.mode} ({preview.broker.current.connection_state})
              </div>
            ) : <div className="text-[10px] text-muted-foreground">No broker connected</div>}
            <div className="space-y-1">
              {BROKER_OPTIONS.map(o => (
                <label key={o.key} className="text-xs flex items-center gap-2 cursor-pointer">
                  <input type="radio" name="broker" value={o.key}
                            checked={brokerChoice === o.key}
                            onChange={() => setBrokerChoice(o.key)}
                            data-testid={`migration-broker-${o.key}`}/>
                  {o.label}
                </label>
              ))}
            </div>
            {brokerChoice && brokerChoice !== "cancel" && (
              <button onClick={() => setStep(3)}
                         className="border border-border/60 px-2 py-1 text-[10px] uppercase tracking-widest hover:bg-border/20"
                         data-testid="migration-to-confirm">
                Continue to Confirm
              </button>
            )}
          </div>
        )}
        {step >= 3 && (
          <div className="space-y-2 pt-3 border-t border-border/40 mt-3">
            <div className="text-[10px] mono uppercase tracking-widest text-muted-foreground">Step 3 — External Backup + Confirm</div>
            <div className="text-[10px] text-muted-foreground">
              JSON exports on the application server are NOT a production backup. Paste a MongoDB Atlas snapshot reference (or equivalent external backup ID) before you can execute.
            </div>
            <input type="text" placeholder="External backup reference (Atlas snapshot ID, etc)"
                     value={backupRef} onChange={e => setBackupRef(e.target.value)}
                     className="w-full bg-transparent border border-border/60 px-2 py-1 text-xs mono"
                     data-testid="migration-backup-ref"/>
            <input type="text" placeholder="Reason (>=6 chars)"
                     value={reason} onChange={e => setReason(e.target.value)}
                     className="w-full bg-transparent border border-border/60 px-2 py-1 text-xs"
                     data-testid="migration-reason"/>
            <input type="text" placeholder="Idempotency key (>=6 chars, e.g. prod-cleanstart-2026-02-04)"
                     value={idem} onChange={e => setIdem(e.target.value)}
                     className="w-full bg-transparent border border-border/60 px-2 py-1 text-xs mono"
                     data-testid="migration-idem"/>
            <div className="text-[10px] text-muted-foreground">Type exactly: <span className="mono pnl-down">{REQUIRED_PHRASE}</span></div>
            <input type="text" placeholder={REQUIRED_PHRASE}
                     value={typed} onChange={e => setTyped(e.target.value)}
                     className="w-full bg-transparent border border-border/60 px-2 py-1 text-xs mono"
                     data-testid="migration-typed"/>
            <button onClick={runConfirm} disabled={busy || !canConfirm}
                       className="border border-red-500 px-2 py-1 text-[10px] uppercase tracking-widest text-red-500 hover:bg-red-500/10 disabled:opacity-40 disabled:cursor-not-allowed"
                       data-testid="migration-execute-btn">
              {busy ? "…" : "Execute Migration"}
            </button>
          </div>
        )}
        {step >= 5 && run && (
          <div className="space-y-2 pt-3 border-t border-border/40 mt-3">
            <div className="text-[10px] mono uppercase tracking-widest text-muted-foreground">Step 4 — Verify</div>
            <div className="text-xs mono">Run: {run.id} · Status: {run.status}</div>
            <button onClick={runVerify} disabled={busy}
                       className="border border-emerald-500 px-2 py-1 text-[10px] uppercase tracking-widest text-emerald-500 hover:bg-emerald-500/10 disabled:opacity-40"
                       data-testid="migration-verify-btn">
              Run Verification
            </button>
            {verify && (
              <div className="border border-border/40 p-2 text-[10px] space-y-0.5" data-testid="migration-verify-result">
                <div className={verify.overall === "PASS" ? "pnl-up" : "pnl-down"}>
                  {verify.overall} — {verify.passed}/{verify.total}
                </div>
                {verify.checks.map(c => (
                  <div key={c.key} className="flex justify-between">
                    <span>{c.key}</span>
                    <span className={c.ok ? "text-emerald-500" : "pnl-down"}>{c.ok ? "✓" : `✗ ${c.actual}`}</span>
                  </div>
                ))}
              </div>
            )}
            <a href={`${api.defaults.baseURL}/admin/production-clean-start/${run.id}/report`}
                target="_blank" rel="noreferrer"
                className="inline-block border border-border/60 px-2 py-1 text-[10px] uppercase tracking-widest hover:bg-border/20"
                data-testid="migration-download-report">
              Download Report
            </a>
          </div>
        )}
      </div>

      {/* Danger Zone: unlock after a prior cutover locked the wizard */}
      {locked && (
        <div className="border border-red-500/60 p-3 space-y-2" data-testid="migration-danger-zone">
          <div className="text-[10px] mono uppercase tracking-widest text-red-500">Danger Zone — Wizard Locked</div>
          <div className="text-[10px] text-muted-foreground">
            A prior production cutover has locked the migration wizard. Type
            <span className="mono px-1 text-red-500"> ENABLE PRODUCTION RESET </span>
            and provide a reason to unlock for a single subsequent reset.
          </div>
          <input type="text" placeholder="Reason (>=6 chars, e.g. 'starting Q2 clean state')"
                   value={unlockReason} onChange={e => setUnlockReason(e.target.value)}
                   className="w-full bg-transparent border border-red-500/60 px-2 py-1 text-xs"
                   data-testid="migration-unlock-reason"/>
          <button onClick={runUnlock}
                     disabled={busy || unlockReason.trim().length < 6}
                     className="border border-red-500 px-2 py-1 text-[10px] uppercase tracking-widest text-red-500 hover:bg-red-500/10 disabled:opacity-40"
                     data-testid="migration-unlock-btn">
            Enable Production Reset
          </button>
        </div>
      )}
    </div>
  );
}
